/**
 * PolyVideo Main App Component
 * 
 * Manages application state and switches between views
 */

import React, { useState, useEffect } from 'react';
import './App.css';
import InputView from './pages/InputView';
import LoadingView from './pages/LoadingView';
import UsageView from './pages/UsageView';
import GraphicsPage from './pages/GraphicsPage';
import { fetchAllData } from './data/apiService';
import { VIEW_STATES } from './data/constants';

function App() {
  const [viewState, setViewState] = useState(VIEW_STATES.INPUT);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [route, setRoute] = useState(window.location.hash);

  useEffect(() => {
    const onHashChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleSubmit = async (inputs) => {
    setViewState(VIEW_STATES.LOADING);
    setError(null);

    try {
      // Convert datetime-local format (user's local time) to EST format expected by backend
      const startTime = formatDateTimeForBackend(inputs.startTime);
      const endTime = formatDateTimeForBackend(inputs.endTime);

      console.log('Converted times:', { startTime, endTime });

      const result = await fetchAllData({
        polymarketUrl: inputs.polymarketUrl,
        youtubeUrl: inputs.youtubeUrl,
        startTime,
        endTime
      });

      setData(result);
      setViewState(VIEW_STATES.USAGE);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setViewState(VIEW_STATES.ERROR);
    }
  };

  // Convert HTML datetime-local format (YYYY-MM-DDTHH:mm) to backend format (YYYY-MM-DD HH:mm:ss)
  // User enters time in EST, we just format it for the backend
  const formatDateTimeForBackend = (dateTimeString) => {
    // Simply replace 'T' with space and add seconds
    // Input: "2025-11-13T15:30"
    // Output: "2025-11-13 15:30:00"
    return dateTimeString.replace('T', ' ') + ':00';
  };

  if (route === '#/graphics') {
    return <GraphicsPage />;
  }

  return (
    <div className="App">
      {viewState === VIEW_STATES.INPUT && (
        <InputView onSubmit={handleSubmit} />
      )}

      {viewState === VIEW_STATES.LOADING && (
        <LoadingView message="Fetching and analyzing data..." />
      )}

      {viewState === VIEW_STATES.USAGE && data && (
        <UsageView data={data} />
      )}

      {viewState === VIEW_STATES.ERROR && (
        <div style={errorStyles.container}>
          <div style={errorStyles.card}>
            <h2 style={errorStyles.title}>❌ Error</h2>
            <p style={errorStyles.message}>{error}</p>
            <button 
              style={errorStyles.button}
              onClick={() => setViewState(VIEW_STATES.INPUT)}
            >
              ← Back to Input
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const errorStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1d2b3a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    maxWidth: '500px',
    width: '100%',
    padding: '48px',
    textAlign: 'center',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ff4976',
    marginBottom: '16px',
  },
  message: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '32px',
    lineHeight: '1.5',
  },
  button: {
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#2b97d4',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

export default App;
